<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { EditIcon } from "@/shared/ui";

import { formatPrice } from "../lib";
import { useEvaluationStore } from "../model";

const {
    chain,
    price = null,
    withState = false,
    editDisabled = false,
} = defineProps<{
    /** Характеристики устройства: бренд, категория, модель... */
    chain: string[];
    /** Оценка устройства. Без неё строка с оценкой не показывается */
    price?: number | null;
    /** Показывать состояние по ответу пользователя, например "Хорошее" */
    withState?: boolean;
    editDisabled?: boolean;
}>();

/** Сноска к оценке: показывается под плиткой, а у цены появляется звёздочка */
const slots = defineSlots<{ note?: () => unknown }>();

const emit = defineEmits<{ edit: [] }>();

const { t } = useI18n({ useScope: "global" });
const evaluationStore = useEvaluationStore();

const formattedPrice = computed(() => (price === null ? null : formatPrice(price)));

const state = computed(() =>
    withState && evaluationStore.answeredGrade
        ? t(`grades.${evaluationStore.answeredGrade}`)
        : null,
);
</script>

<!-- Плитка сдаваемого устройства -->
<template>
    <div class="handed-device">
        <div class="device">
            <button
                type="button"
                class="edit"
                :aria-label="t('handedDevice.edit')"
                :disabled="editDisabled"
                @click="emit('edit')"
            >
                <EditIcon />
            </button>

            <img class="image" :src="evaluationStore.deviceImage" alt="" />

            <div class="body">
                <p class="title">{{ t("handedDevice.title") }}</p>

                <ul class="chain">
                    <li v-for="(item, index) in chain" :key="index">
                        <span :title="item">{{ item }}</span>
                    </li>
                    <li v-if="state">{{ state }}</li>
                </ul>

                <div v-if="formattedPrice" class="price">
                    <span>{{ t("handedDevice.valuation") }}</span>
                    <b>
                        {{ formattedPrice }} {{ evaluationStore.currency }}
                        <sup v-if="slots.note" class="asterisk">*</sup>
                    </b>
                </div>
            </div>
        </div>

        <p v-if="slots.note" class="note">
            <span class="asterisk">*</span>
            <span><slot name="note" /></span>
        </p>
    </div>
</template>

<style scoped>
.handed-device {
    margin-bottom: 16px;
    padding: 12px 16px;
    border-radius: 8px;
    background-color: #f4f6f8;
    font-size: var(--custom-font-size);
    user-select: none;

    @media screen and (max-width: 767px) {
        padding: 8px;
    }
}

.device {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 12px;

    @media screen and (max-width: 767px) {
        gap: 8px;
    }
}

.edit {
    position: absolute;
    top: 0;
    right: 0;
    padding: 0;
    border: none;
    background: none;
    color: var(--custom-color-gray);
    line-height: 0;
    cursor: pointer;

    &:hover {
        color: var(--custom-color-main-hover, var(--custom-color-main));
    }

    &:disabled {
        cursor: not-allowed;
    }
}

.image {
    flex: 0 0 72px;
    width: 72px;
    height: 72px;
    object-fit: contain;
}

.body {
    flex: 1;
    min-width: 0;
}

.title {
    margin: 0 0 4px;
    font-weight: 700;
    line-height: 1.429;
}

.chain {
    margin: 0 0 4px;
    padding: 0;
    line-height: 1.429;

    li {
        display: inline;

        &:not(:last-child)::after {
            content: " • ";
        }

        > span {
            display: inline-block;
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            vertical-align: bottom;
        }
    }
}

.price {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
}

.asterisk {
    color: var(--custom-color-gray);
    font-weight: 400;
}

.note {
    display: flex;
    gap: 8px;
    margin: 12px 0 0;
    padding-top: 12px;
    border-top: 1px solid #d3dce9;
    font-size: calc(var(--custom-font-size) * 0.857);
    line-height: 1.333;
}
</style>
